import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Callout } from '@repo/ui/components/ui/callout'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/ui/table'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { ReportRouteShell } from '@/components/app/route-shells'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'
import { trpc } from '@/lib/trpc'

export const Route = createFileRoute('/_app/admin/users')({
  component: AdminUsersPage,
  staticData: {
    breadcrumbs: [{ label: 'Users' }],
  } satisfies RouteStaticData,
})

function AdminUsersPage() {
  const [search, setSearch] = useState('')
  const [offset, setOffset] = useState(0)

  const usersQuery = trpc.admin.users.list.useQuery({
    search: search || undefined,
    limit: 20,
    offset,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  })

  const users = usersQuery.data?.users ?? []
  const hasMore = usersQuery.data?.hasMore ?? false

  return (
    <ReportRouteShell>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            setOffset(0)
          }}
          className="h-9 w-56 rounded-md border border-input bg-background px-3 text-sm"
          placeholder="Search users..."
        />
      </div>
      {usersQuery.isError ? (
        <Callout variant="destructive">
          <p className="text-sm text-muted-foreground">
            Failed to load users. {(usersQuery.error as unknown as Error).message}
          </p>
        </Callout>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                    {usersQuery.isLoading ? 'Loading users...' : 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name ?? 'Unnamed'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOffset((prev) => Math.max(prev - 20, 0))}
              disabled={offset === 0 || usersQuery.isLoading}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOffset((prev) => prev + 20)}
              disabled={!hasMore || usersQuery.isLoading}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </ReportRouteShell>
  )
}
